import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles } from 'vopay';
import { useToast } from '@/providers/Toast.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import useFetchPartnerAccountStatusSummary from '@/components/Entities/hooks/useApi/useFetchPartnerAccountStatusSummary';
import ProgramTermsCard from './ProgramTermsCard';
import AccountStatusSummaryCard from './AccountStatusSummaryCard';
import getProgramTermsVariant from './programTermsVariant';
import getOnboardingPipeline from './onboardingPipeline';
import styles from './AccountsOverview.module.scss';

const cn = withStyles(styles);

type AccountsOverviewProps = {
  /** True until the accounts listing (which carries the terms) has responded. */
  fetchingProgramTerms: boolean;
  revShare: string | null;
  minimumProgramFee: string | null;
};

export default function AccountsOverview({ fetchingProgramTerms, revShare, minimumProgramFee }: AccountsOverviewProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountsOverview' });
  const toast = useToast();

  const {
    response: { partnerTypeLabel },
  } = usePartnerData();

  const { permissions } = usePermissions();

  const programTermsVariant = getProgramTermsVariant(partnerTypeLabel);
  const pipelineStages = getOnboardingPipeline(partnerTypeLabel, permissions.partnerLiability);

  const {
    fetching: fetchingStatusSummary,
    numTotalAccounts,
    statusCounts,
    fetchPartnerAccountStatusSummary,
  } = useFetchPartnerAccountStatusSummary();

  // The hook's `fetching` is false until the mount effect below runs; without this the card would
  // flash zeroed totals for one frame first.
  const [hasLoadedStatusSummary, setHasLoadedStatusSummary] = useState(false);

  // Fetched once on mount; the summary is independent of the list's filters and pagination.
  useEffect(() => {
    fetchPartnerAccountStatusSummary()
      .catch((error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(
          t('loadError', { defaultValue: 'Could not load the accounts overview: {{errorMessage}}', errorMessage }),
        );
      })
      .finally(() => setHasLoadedStatusSummary(true));
  }, []);

  return (
    <div className={cn('overview')}>
      {programTermsVariant && (
        <ProgramTermsCard
          variant={programTermsVariant}
          fetching={fetchingProgramTerms}
          revShare={revShare}
          minimumProgramFee={minimumProgramFee}
        />
      )}

      <AccountStatusSummaryCard
        fetching={fetchingStatusSummary || !hasLoadedStatusSummary}
        numTotalAccounts={numTotalAccounts}
        statusCounts={statusCounts}
        pipelineStages={pipelineStages}
      />
    </div>
  );
}
